import { Location } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, ReplaySubject, SchedulerLike } from 'rxjs';
import { delay, distinctUntilChanged, map, skipWhile, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { FEATURE_BULK_ACTIONS_V2 } from '../flags';
import { ActionList } from './action-list';
import { ActionListFetcher, ActionListsStoreService } from './action-lists.store.service';
import { Action } from './actions';
import { ActiveWorkReporter, BulkActionsProgressReporter } from './history/bulk-actions-reporter.service';

@Injectable({ providedIn: 'root' })
export class ActionListsDetailsStoreService {
  tabs = ['accounts', 'history'];
  availableActions$: Observable<Action[]>;
  historyListInProgress$$ = new ReplaySubject<boolean>(1);
  actionDisabled$: Observable<boolean>;
  private actionListId: string;
  private actionList$$: ReplaySubject<ActionList> = new ReplaySubject(1);
  actionList$: Observable<ActionList> = this.actionList$$.asObservable();

  constructor(
    @Inject(ActionListsStoreService) private actionListStoreService: ActionListFetcher,
    @Inject(FEATURE_BULK_ACTIONS_V2) private readonly v2ActionsEnabled$: Observable<boolean>,
    @Inject(BulkActionsProgressReporter) private progress: ActiveWorkReporter,
    private location: Location,
    @Inject('') @Optional() sched?: SchedulerLike,
  ) {
    sched = sched || undefined; // RXJS cannot handle null
    this.availableActions$ = this.actionList$.pipe(
      switchMap((actionList) => {
        return this.actionListStoreService.getAvailableActions(actionList.id);
      }),
    );
    this.actionDisabled$ = v2ActionsEnabled$.pipe(
      switchMap((enabled) =>
        enabled ? this.buildDisabledViaBulkActions$(sched) : this.buildDisabledViaActionListData$(sched),
      ),
    );
  }

  initializeStore(actionListId: string): void {
    this.actionListId = actionListId;
    this.reloadActionListDataFromAA();
  }

  signalHistoryInProgress(progress: boolean): void {
    this.historyListInProgress$$.next(progress);
    this.reloadActionListDataFromAA();
  }

  getTabIndex(tabId: string): number {
    const tabIndex = this.tabs.indexOf(tabId);
    return tabIndex >= 0 ? tabIndex : 0;
  }

  updateTabRoute(tabIndex: number): void {
    const activeTab = this.tabs[tabIndex];
    const route = `action-lists/${this.actionListId}/${activeTab}`;
    let query = '';
    if (this.location.path().indexOf('?') > 0) {
      query = this.location.path().split('?')[1];
    }
    this.location.go(route, query);
  }

  deleteActionList(actionList: ActionList): void {
    this.actionListStoreService.deleteActionList(actionList, true);
  }

  private buildDisabledViaBulkActions$(sched: SchedulerLike): Observable<boolean> {
    return this.actionList$.pipe(
      switchMap(() => {
        const stopAfterActiveWorkFalse$ = this.progress.hasActiveWork$.pipe(
          skipWhile((v) => v === false), // skip initial set of false
          skipWhile((v) => v === true), // skip first true after that
          delay(10, sched), // emit *after* the next false value
        );
        return this.progress.hasActiveWork$.pipe(takeUntil(stopAfterActiveWorkFalse$), startWith(true));
      }),
      startWith(true),
      distinctUntilChanged(),
    );
  }

  private buildDisabledViaActionListData$(_sched: SchedulerLike): Observable<boolean> {
    return this.actionList$.pipe(map((v) => v.inProgress));
  }

  private reloadActionListDataFromAA(): void {
    this.actionListStoreService
      .getActionList(this.actionListId)
      .pipe(take(1))
      .subscribe((al) => this.actionList$$.next(al));
  }
}
